<template>
    <component
        :is="rootComponent"
        v-bind="rootProps"
        class="
            d-block
            w-100
            h-100
            no-underline-link
        "
    >
        <div
            class="
                d-flex
                flex-column
                w-100
                h-100
            "
        >

            <!-- Media -->
            <div
                class="
                    d-flex
                    flex-column
                    flex-grow-1
                    overflow-hidden
                    align-center
                    justify-center
                "
                :class="[
                    mediaPadClass,
                    {
                        'order-1': modelValue.mediaSide === 'top',
                        'order-2': modelValue.mediaSide === 'bottom'
                    }
                ]"
            >
                <template
                    v-if="modelValue.altImage && modelValue.image"
                >
                    <FullImage
                        v-if="isMobile"
                        :image-object="modelValue.altImage"
                        :type="isMobile ? 'medium' : 'large'"
                        :style="mediaStyle"
                        scalable
                    />
                    <FullImage
                        v-else
                        :image-object="modelValue.image"
                        :type="isMobile ? 'medium' : 'large'"
                        :style="mediaStyle"
                        scalable
                    />
                </template>
                <template
                    v-else-if="modelValue.image"
                >
                    <FullImage
                        :image-object="modelValue.image"
                        :type="isMobile ? 'medium' : 'large'"
                        :style="mediaStyle"
                        scalable
                    />
                </template>
                <template
                    v-else-if="modelValue.videoSm && isMobile"
                >
                    <BlockTypeVideoStatic
                        :model-value="modelValue.videoSm"
                    />
                </template>
                <template
                    v-else-if="modelValue.videoLg"
                >
                    <BlockTypeVideoStatic
                        :model-value="modelValue.videoLg"
                    />
                </template>
            </div>

            <!-- Text -->
            <div
                class="
                    d-flex
                    flex-column
                "
                :class="[
                    textPositionClasses,
                    {
                        'order-2': modelValue.mediaSide === 'top',
                        'order-1': modelValue.mediaSide === 'bottom'
                    }
                ]"
            >
                <div
                    class="
                        fit-content
                        rounded-lg
                    "
                    :class="[
                        textAlignClass,
                        textThemeClass,
                        textPadClass,
                        textOverlayClass
                    ]"
                >
                    <div
                        v-if="modelValue.headingImage && !isHeadingImageError"
                        class="
                            pa-0
                            ma-0
                        "
                    >
                        <v-img
                            :src="modelValue.headingImage.url"
                            :alt="modelValue.headingImage.title"
                            :aspect-ratio="modelValue.headingImage.aspectRatio"
                            width="auto"
                            :height="`calc(60px * ${modelValue.headingImage.scale})`"
                            class="mx-auto"
                            @error="onHeadingImageError"
                        />
                    </div>
                    <h2
                        v-else
                        class="
                            text-display-medium
                            text-md-display-large
                            font-weight-bold
                            text-uppercase
                            pa-0
                            ma-0
                        "
                        :class="[
                            textStrokeClass,
                            {
                                'text-drop-shadow': modelValue.headingDropShadow
                            }
                        ]"
                    >
                        {{ modelValue.heading }}
                    </h2>
                    <div
                        v-if="modelValue.subHeading"
                        class="
                            mt-2
                            text-headline-small
                            text-md-headline-medium
                        "
                        :class="[
                            textStrokeClass,
                            {
                                'text-drop-shadow': modelValue.subHeadingDropShadow
                            }
                        ]"
                    >
                        {{ modelValue.subHeading }}
                    </div>
                    <div
                        v-if="buttons.length"
                        class="
                            d-flex
                            flex-wrap
                            justify-center
                            mt-4
                        "
                    >
                        <BtnUniversal
                            v-for="(button, index) in buttons"
                            :key="`hero-carousel-item-btn-${index}`"
                            :params="{ content: button.label, path: button.link }"
                            :is-nested-link="isBlockLink"
                            :theme="buttonTheme"
                            color="secondary"
                            :size="isMobile ? 'default' : 'large'"
                            class="ma-2"
                            :class="{
                                'bg-secondary': isBlockLink
                            }"
                            unset-default-label-case
                        >
                            {{ button.label }}
                        </BtnUniversal>
                    </div>
                </div>
            </div>
        </div>
    </component>
</template>

<script lang="ts" setup>
    import type {
        HeroCarouselItemYSplitV1
    } from '~/types';
    import {
        useContentBlocksUtils
    } from '~/composables';

    const props = defineProps<{
        modelValue: HeroCarouselItemYSplitV1['values'];
        isMobile: boolean;
        breakpoint: string;
        breakpointName: string;
    }>();

    const FullImage = defineAsyncComponent(() => import('~/components/media/FullImage/FullImage.vue'));
    const BlockTypeVideoStatic = defineAsyncComponent(() => import('~/components/content/blocks/v2/BlockTypeVideoStatic/BlockTypeVideoStatic.vue'));
    const BtnUniversal = defineAsyncComponent(() => import('~/components/BtnUniversal/BtnUniversal.vue'));

    const NuxtLink = resolveComponent('NuxtLink');

    const isMobile = computed(() => props.isMobile);
    const { getHeroCarouselItemBindings } = useContentBlocksUtils();

    const {
        buttons,
        isBlockLink,
        rootComponent,
        rootProps,
        textPositionClasses,
        textAlignClass,
        textThemeClass,
        textStrokeClass,
        buttonTheme,
        textOverlayClass,
        textPadClass,
        mediaPadClass
    } = getHeroCarouselItemBindings(() => props.modelValue, () => isMobile.value, NuxtLink);

    const isHeadingImageError = ref<boolean>(false);

    function onHeadingImageError(): void {
        isHeadingImageError.value = true;
    }

    const mediaStyle = computed(() => {
        const useAltImage = isMobile.value && !!props.modelValue.altImage;
        const widthOverride = useAltImage
            ? props.modelValue.altImageWidthOverride
            : props.modelValue.imageWidthOverride;
        const heightOverride = useAltImage
            ? props.modelValue.altImageHeightOverride
            : props.modelValue.imageHeightOverride;
        const imageFit = useAltImage
            ? props.modelValue.altImageFit
            : props.modelValue.imageFit;
        const imagePosition = useAltImage
            ? props.modelValue.altImagePosition
            : props.modelValue.imagePosition;
        return {
            width: widthOverride || 'auto',
            'max-height': heightOverride || '100%',
            'object-fit': imageFit || 'unset',
            'object-position': imagePosition || 'unset'
        };
    });
</script>

<style lang="scss"></style>
